import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { examApi } from '../services/exam.service';
import {
  GuestExamAccess,
  GuestExamAttemptResult,
  PublicDashboardExam,
  Question,
} from '../types/exam';

const formatDate = (value?: string) => {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

type ExamOption = NonNullable<Question['options']>[number];

const getExamOptionIdentity = (option: ExamOption, index: number) =>
  String(option.id || option.optionId || option.key || option.text || index);

const getExamOptionAnswerValue = (option: ExamOption, index: number) =>
  String(option.text || option.id || option.optionId || option.key || index);

const isExamOptionSelected = (savedAnswer: string | undefined, option: ExamOption, index: number) => {
  if (!savedAnswer) return false;
  return savedAnswer === getExamOptionAnswerValue(option, index) ||
    savedAnswer === getExamOptionIdentity(option, index) ||
    savedAnswer === String(index);
};

const PublicExamDashboard: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exams, setExams] = useState<PublicDashboardExam[]>([]);
  const [selectedExam, setSelectedExam] = useState<PublicDashboardExam | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [schoolForm, setSchoolForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    accessCode: '',
  });
  const [inviteForm, setInviteForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
  });
  const [guestAccess, setGuestAccess] = useState<GuestExamAccess | null>(null);
  const [guestAttempt, setGuestAttempt] = useState<GuestExamAttemptResult | null>(null);
  const [guestQuestions, setGuestQuestions] = useState<Question[]>([]);
  const [guestAnswers, setGuestAnswers] = useState<Record<string, string>>({});
  const [studentInvite, setStudentInvite] = useState<{ shareUrl: string; inviteCode: string } | null>(null);

  const activeCategory = String(searchParams.get('category') || '').trim().toUpperCase();
  const inviteCode = String(searchParams.get('invite') || '').trim().toUpperCase();
  const paymentLinkId = String(searchParams.get('razorpay_payment_link_id') || '').trim();

  useEffect(() => {
    const loadExams = async () => {
      try {
        setLoading(true);
        const response = await examApi.getPublicDashboardExams(activeCategory || undefined);
        setExams(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Failed to load public exams:', error);
        setExams([]);
      } finally {
        setLoading(false);
      }
    };

    void loadExams();
  }, [activeCategory]);

  useEffect(() => {
    const examId = String(searchParams.get('exam') || '').trim();
    if (!examId) {
      setSelectedExam(null);
      return;
    }

    const loadExam = async () => {
      try {
        setDetailLoading(true);
        const response = await examApi.getPublicDashboardExamById(examId);
        setSelectedExam(response);
      } catch (error) {
        console.error('Failed to load public exam detail:', error);
        setSelectedExam(null);
      } finally {
        setDetailLoading(false);
      }
    };

    void loadExam();
  }, [searchParams]);

  useEffect(() => {
    if (!inviteCode || !paymentLinkId) return;
    const verify = async () => {
      try {
        const access = await examApi.verifyReferralGuestPayment(inviteCode, paymentLinkId);
        setGuestAccess(access);
      } catch (error) {
        console.error('Failed to verify referral guest payment:', error);
      }
    };
    void verify();
  }, [inviteCode, paymentLinkId]);

  useEffect(() => {
    if (!guestAttempt?.attemptId || !guestAttempt?.accessToken) return;
    const loadQuestions = async () => {
      try {
        const questions = await examApi.getGuestAttemptQuestions(
          guestAttempt.attemptId,
          guestAttempt.accessToken,
        );
        setGuestQuestions(Array.isArray(questions) ? questions : []);
      } catch (error) {
        console.error('Failed to load guest attempt questions:', error);
      }
    };
    void loadQuestions();
  }, [guestAttempt?.attemptId, guestAttempt?.accessToken]);

  const registrationLink = useMemo(() => '/register', []);

  const copy = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    window.alert(message);
  };

  const createStudentInvite = async () => {
    if (!selectedExam?.examId) return;
    try {
      setBusy(true);
      const response = await examApi.createReferralInvite(selectedExam.examId);
      setStudentInvite({ shareUrl: response.shareUrl, inviteCode: response.inviteCode });
      await copy(response.shareUrl, 'Referral exam invite copied.');
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to create referral invite');
    } finally {
      setBusy(false);
    }
  };

  const unlockViaSchoolCode = async () => {
    if (!selectedExam?.examId) return;
    try {
      setBusy(true);
      const access = await examApi.createSchoolGuestAccess(selectedExam.examId, schoolForm);
      setGuestAccess(access);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to validate school access');
    } finally {
      setBusy(false);
    }
  };

  const unlockViaReferralPayment = async () => {
    if (!inviteCode) return;
    try {
      setBusy(true);
      const response = await examApi.initiateReferralGuestPayment(inviteCode, inviteForm);
      if (response.accessToken) {
        setGuestAccess(response);
      } else if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to initiate exam access payment');
    } finally {
      setBusy(false);
    }
  };

  const startGuestAttempt = async () => {
    if (!selectedExam?.examId || !guestAccess?.accessToken) return;
    try {
      setBusy(true);
      const attempt = await examApi.startGuestAttempt(selectedExam.examId, guestAccess.accessToken);
      setGuestAttempt(attempt);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to start exam attempt');
    } finally {
      setBusy(false);
    }
  };

  const saveGuestAnswer = async (questionId: string, answer: string) => {
    if (!guestAttempt?.attemptId || !guestAttempt.accessToken) return;
    setGuestAnswers((prev) => ({ ...prev, [questionId]: answer }));
    try {
      await examApi.saveGuestAttemptAnswer(guestAttempt.attemptId, guestAttempt.accessToken, {
        questionId,
        answer,
        timeSpent: 0,
      });
    } catch (error) {
      console.error('Failed to save guest answer:', error);
    }
  };

  const submitGuestAttempt = async () => {
    if (!guestAttempt?.attemptId || !guestAttempt.accessToken) return;
    try {
      setBusy(true);
      const result = await examApi.submitGuestAttempt(guestAttempt.attemptId, guestAttempt.accessToken, {
        totalTimeSpent: 0,
      });
      setGuestAttempt(result);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to submit guest attempt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f7f4ea_0%,_#fffdf8_36%,_#f3efe2_100%)] text-slate-900">
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[2.5rem] border border-amber-200 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.22),_transparent_28%),linear-gradient(135deg,_#111827_0%,_#1f2937_42%,_#78350f_100%)] p-8 text-white shadow-[0_32px_90px_-48px_rgba(17,24,39,0.85)]">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_340px]">
            <div>
              <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-amber-100">
                Public exam dashboard
              </span>
              <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
                Public exam access for schools, institutes, and paid referral invites.
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-6 text-amber-50 sm:text-base">
                Schools can distribute one shared code for direct exam access. Registered students can generate paid invite links that unlock one exam attempt only, without creating a platform account.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={user?.role === 'STUDENT' ? '/student/exams' : registrationLink}
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white"
                >
                  {user?.role === 'STUDENT' ? 'Open student exams' : 'Register for full platform access'}
                </Link>
                {inviteCode ? (
                  <div className="rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white">
                    Referral invite: {inviteCode}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-white/10 bg-white/10 p-5 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-100">Dashboard modes</p>
              <div className="mt-4 grid gap-3">
                {[
                  { label: 'Grand tests', value: 'GRAND_TEST' },
                  { label: 'Talent tests', value: 'TALENT_TEST' },
                  { label: 'All public exams', value: '' },
                ].map((option) => (
                  <button
                    key={option.label}
                    onClick={() =>
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        if (option.value) next.set('category', option.value);
                        else next.delete('category');
                        return next;
                      })
                    }
                    className={`rounded-[1.25rem] border px-4 py-3 text-left text-sm ${
                      activeCategory === option.value || (!activeCategory && option.value === '')
                        ? 'border-white/25 bg-white/15 text-white'
                        : 'border-white/10 bg-slate-950/20 text-slate-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
          <div className="space-y-5">
            {loading ? (
              <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
                Loading public exams...
              </div>
            ) : exams.length === 0 ? (
              <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
                No exams are enabled for this dashboard yet.
              </div>
            ) : (
              exams.map((exam) => (
                <article key={exam.examId} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                          {exam.publicDashboardCategory.replace('_', ' ')}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                          {exam.subject}
                        </span>
                        {exam.schoolAccessEnabled ? (
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                            School code enabled
                          </span>
                        ) : null}
                        {exam.guestReferralEnabled ? (
                          <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-800">
                            Referral fee INR {Number(exam.guestAttemptFee || 0).toFixed(2)}
                          </span>
                        ) : null}
                      </div>
                      <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">{exam.title}</h2>
                      <p className="mt-2 text-sm text-slate-500">
                        {exam.publicDashboardOrganizer} | Starts {formatDate(exam.startDate)}
                      </p>
                    </div>
                    <Link
                      to={`/exam/${exam.examId}`}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      View details
                    </Link>
                  </div>
                </article>
              ))
            )}
          </div>

          <aside className="rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">Exam access workspace</h2>
              <p className="mt-1 text-sm text-slate-500">Select an exam to unlock and attempt it here.</p>
            </div>
            <div className="space-y-5 px-6 py-5">
              {detailLoading ? (
                <p className="text-sm text-slate-500">Loading exam detail...</p>
              ) : !selectedExam ? (
                <p className="text-sm text-slate-500">Pick an exam to inspect its access model and launch the attempt flow.</p>
              ) : (
                <>
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-lg font-semibold text-slate-950">{selectedExam.title}</p>
                    <p className="mt-2 text-sm text-slate-600">{selectedExam.publicDashboardOrganizer}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{selectedExam.description}</p>
                  </div>

                  {user?.role === 'STUDENT' && selectedExam.guestReferralEnabled ? (
                    <div className="rounded-[1.5rem] border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student referral invite</p>
                      <p className="mt-3 text-sm text-slate-600">
                        Create a paid one-time exam invite for a friend. This does not register them in the platform.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          onClick={() => void createStudentInvite()}
                          disabled={busy}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          {busy ? 'Generating...' : 'Generate referral invite'}
                        </button>
                        {studentInvite ? (
                          <button
                            onClick={() => void copy(studentInvite.shareUrl, 'Referral invite copied.')}
                            className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"
                          >
                            Copy invite again
                          </button>
                        ) : null}
                      </div>
                      {studentInvite ? (
                        <p className="mt-3 text-xs text-slate-500">Invite code: {studentInvite.inviteCode}</p>
                      ) : null}
                    </div>
                  ) : null}

                  {!guestAttempt && !guestAccess && selectedExam.schoolAccessEnabled ? (
                    <div className="rounded-[1.5rem] border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School access</p>
                      <div className="mt-4 grid gap-3">
                        <input
                          value={schoolForm.guestName}
                          onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestName: e.target.value }))}
                          placeholder="Candidate name"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          value={schoolForm.guestEmail}
                          onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestEmail: e.target.value }))}
                          placeholder="Email (optional)"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          value={schoolForm.guestPhone}
                          onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestPhone: e.target.value }))}
                          placeholder="Phone (optional)"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          value={schoolForm.accessCode}
                          onChange={(e) => setSchoolForm((prev) => ({ ...prev, accessCode: e.target.value.toUpperCase() }))}
                          placeholder="Enter common school code"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <button
                          onClick={() => void unlockViaSchoolCode()}
                          disabled={busy}
                          className="rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                        >
                          {busy ? 'Validating...' : 'Unlock via school code'}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {!guestAttempt && !guestAccess && inviteCode && selectedExam.guestReferralEnabled ? (
                    <div className="rounded-[1.5rem] border border-slate-200 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Referral payment unlock</p>
                      <p className="mt-3 text-sm text-slate-600">
                        This invite unlocks one exam attempt only. It does not create a platform account.
                      </p>
                      <div className="mt-4 grid gap-3">
                        <input
                          value={inviteForm.guestName}
                          onChange={(e) => setInviteForm((prev) => ({ ...prev, guestName: e.target.value }))}
                          placeholder="Candidate name"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          value={inviteForm.guestEmail}
                          onChange={(e) => setInviteForm((prev) => ({ ...prev, guestEmail: e.target.value }))}
                          placeholder="Email (optional)"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          value={inviteForm.guestPhone}
                          onChange={(e) => setInviteForm((prev) => ({ ...prev, guestPhone: e.target.value }))}
                          placeholder="Phone (optional)"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                        <button
                          onClick={() => void unlockViaReferralPayment()}
                          disabled={busy}
                          className="rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                        >
                          {busy ? 'Redirecting...' : `Pay INR ${Number(selectedExam.guestAttemptFee || 0).toFixed(2)} to unlock`}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {guestAccess && !guestAttempt ? (
                    <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                      <p className="text-sm font-semibold text-emerald-900">
                        Access unlocked for {guestAccess.guestName || 'candidate'}.
                      </p>
                      <p className="mt-2 text-sm text-emerald-700">
                        Mode: {guestAccess.accessType === 'SCHOOL_CODE' ? 'School code' : 'Paid referral'}.
                      </p>
                      <button
                        onClick={() => void startGuestAttempt()}
                        disabled={busy}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                      >
                        <PlayIcon className="h-4 w-4" />
                        {busy ? 'Starting...' : 'Start exam'}
                      </button>
                    </div>
                  ) : null}

                  {guestAttempt ? (
                    <div className="space-y-4 rounded-[1.5rem] border border-slate-200 p-4">
                      <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                        <span>Status: {guestAttempt.status}</span>
                        {guestAttempt.status === 'GRADED' && guestAttempt.resultsPublished === false ? (
                          <span>Result pending publication</span>
                        ) : (
                          <>
                            <span>Score: {guestAttempt.totalScore}</span>
                            <span>Percent: {guestAttempt.percentageScore}%</span>
                            <span>Grade: {guestAttempt.grade || '-'}</span>
                          </>
                        )}
                      </div>
                      {guestAttempt.status !== 'GRADED' ? (
                        <>
                          {guestQuestions.map((question) => (
                            <div key={question.questionId} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                              <p className="text-sm font-semibold text-slate-950">
                                Q{question.sequenceNumber}. {question.questionText}
                              </p>
                              <div className="mt-3 space-y-2">
                                {(question.options || []).map((option, optionIndex) => {
                                  const optionValue = getExamOptionAnswerValue(option, optionIndex);
                                  const selected = isExamOptionSelected(
                                    guestAnswers[question.questionId],
                                    option,
                                    optionIndex,
                                  );
                                  return (
                                  <label key={getExamOptionIdentity(option, optionIndex)} className="flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                      type="radio"
                                      name={question.questionId}
                                      value={optionValue}
                                      checked={selected}
                                      onChange={() => void saveGuestAnswer(question.questionId, optionValue)}
                                    />
                                    <span>{option.text}</span>
                                  </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          {guestQuestions.length > 0 ? (
                            <button
                              onClick={() => void submitGuestAttempt()}
                              disabled={busy}
                              className="rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                            >
                              {busy ? 'Submitting...' : 'Submit exam'}
                            </button>
                          ) : (
                            <p className="text-sm text-slate-500">Loading questions...</p>
                          )}
                        </>
                      ) : (
                        <div className={`rounded-[1.25rem] border p-4 text-sm ${
                          guestAttempt.resultsPublished === false
                            ? 'border-amber-200 bg-amber-50 text-amber-800'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        }`}>
                          {guestAttempt.resultsPublished === false
                            ? 'Your attempt has been submitted. Results will appear after the teacher publishes them.'
                            : `Result ready. ${guestAttempt.isPassed ? 'Passed' : 'Not passed'} with ${guestAttempt.percentageScore}%.`}
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Question preview</p>
                    {!selectedExam.questionsPreview?.length ? (
                      <p className="mt-3 text-sm text-slate-500">Question preview is not available for this exam.</p>
                    ) : (
                      <div className="mt-4 space-y-3">
                        {selectedExam.questionsPreview.map((question) => (
                          <div key={question.questionId} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-sm font-semibold text-slate-950">{question.questionText}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {question.questionType} | {question.marks} marks | {question.difficulty || 'Unspecified difficulty'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Platform access</p>
                    <p className="mt-3 text-sm text-slate-700">
                      Exam guest access is separate from application registration. Use the main registration flow only if you want classes or the rest of the platform.
                    </p>
                    <Link
                      to={registrationLink}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      Open platform registration
                    </Link>
                  </div>
                </>
              )}
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
};

export default PublicExamDashboard;
